"use client";

import React, { useState } from "react";
import { ShieldCheck, SlidersHorizontal, ArrowRight, FileText, CircleCheck, FlaskConical, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface MaterialOption {
  grade: string;
  name: Record<Lang, string>;
  corrosionRating: Record<Lang, string>;
  primaryApplication: Record<Lang, string>;
  characteristics: Record<Lang, string>;
  pittingPREN: string;
}
const materialOptions: MaterialOption[] = [{
  grade: "AISI 316L",
  name: {
    en: "Low-Carbon Molybdenum Stainless",
    zh: "含钼低碳耐蚀奥氏体不锈钢",
    es: "Acero inoxidable austenítico bajo en carbono con molibdeno",
    de: "Kohlenstoffarmer Molybdän-Edelstahl",
    fr: "Acier inoxydable austénitique bas carbone au molybdène",
    pt: "Aço inoxidável austenítico de baixo carbono com molibdênio"
  },
  corrosionRating: {
    en: "High (Chloride Resistant)",
    zh: "高耐腐蚀（抗氯化物点蚀）",
    es: "Alta (resistente a cloruros)",
    de: "Hoch (chloridbeständig)",
    fr: "Élevée (résistant aux chlorures)",
    pt: "Alta (resistente a cloretos)"
  },
  primaryApplication: {
    en: "Chemical, Marine & Pharmaceutical Filtration",
    zh: "化工、海洋工程与制药精密过滤",
    es: "Filtración química, marina y farmacéutica",
    de: "Chemie-, Marine- und Pharmafiltration",
    fr: "Filtration chimique, marine et pharmaceutique",
    pt: "Filtração química, marítima e farmacêutica"
  },
  characteristics: {
    en: "Resists pitting in aggressive halide and acid environments.",
    zh: "在强腐蚀性卤化物和酸性工况下具备优异的抗点蚀与缝隙腐蚀性能。",
    es: "Resiste picaduras en entornos agresivos con haluros y ácidos.",
    de: "Beständig gegen Lochfraß in aggressiven Halogenid- und Säureumgebungen.",
    fr: "Résiste aux piqûres dans les environnements agressifs d'halogénures et d'acides.",
    pt: "Resiste à corrosão por pite em meios agressivos com haletos e ácidos."
  },
  pittingPREN: "23-25"
}, {
  grade: "AISI 304",
  name: {
    en: "Standard Austenitic Stainless",
    zh: "标准奥氏体通用不锈钢",
    es: "Acero inoxidable austenítico estándar",
    de: "Standard austenitischer Edelstahl",
    fr: "Acier inoxydable austénitique standard",
    pt: "Aço inoxidável austenítico padrão"
  },
  corrosionRating: {
    en: "Moderate Industrial",
    zh: "中等工业耐腐蚀",
    es: "Industrial moderada",
    de: "Mäßig industriell",
    fr: "Industrielle modérée",
    pt: "Industrial moderada"
  },
  primaryApplication: {
    en: "Food, Water & General Screening",
    zh: "食品卫生、水处理与通用工业筛分",
    es: "Alimentos, agua y tamizado industrial general",
    de: "Lebensmittel, Wasser und allgemeine Siebung",
    fr: "Alimentaire, eau et criblage industriel général",
    pt: "Alimentos, água e peneiramento industrial geral"
  },
  characteristics: {
    en: "Cost-effective baseline for non-acidic aqueous filtration.",
    zh: "适用于非酸性水溶液介质与通用粉体筛分的高性价比基准材质。",
    es: "Base rentable para filtración acuosa no ácida.",
    de: "Wirtschaftliche Basis für nicht-saure wässrige Filtration.",
    fr: "Référence économique pour la filtration aqueuse non acide.",
    pt: "Referência econômica para filtração aquosa não ácida."
  },
  pittingPREN: "18-20"
}, {
  grade: "AISI 314",
  name: {
    en: "High-Temperature Austenitic Stainless",
    zh: "高耐热抗氧化奥氏体不锈钢",
    es: "Acero inoxidable austenítico de alta temperatura",
    de: "Hochwarmfester austenitischer Edelstahl",
    fr: "Acier inoxydable austénitique réfractaire haute température",
    pt: "Aço inoxidável austenítico de alta temperatura"
  },
  corrosionRating: {
    en: "High Thermal & Oxidation",
    zh: "极佳耐高温与抗高温氧化性能",
    es: "Alta resistencia térmica y a la oxidación",
    de: "Hohe Temperatur- & Oxidationsbeständigkeit",
    fr: "Haute résistance thermique et à l'oxydation",
    pt: "Alta resistência térmica e à oxidação"
  },
  primaryApplication: {
    en: "Thermal Processing & Hot Gas Filtration",
    zh: "热处理炉膛防护与高温烟气过滤",
    es: "Tratamiento térmico y filtración de gases calientes",
    de: "Thermische Prozesse & Heißgasfiltration",
    fr: "Procédés thermiques et filtration des gaz chauds",
    pt: "Processamento térmico e filtração de gases quentes"
  },
  characteristics: {
    en: "Excellent scale resistance and strength in continuous high-temperature atmospheres.",
    zh: "在持续高温富氧或保护性气氛中具有出色的抗结皮氧化和高温机械强度。",
    es: "Excelente resistencia a la oxidación superficial y resistencia mecánica a alta temperatura continua.",
    de: "Ausgezeichnete Zunderbeständigkeit und Festigkeit bei kontinuierlichen Hochtemperaturen.",
    fr: "Excellente résistance au calaminage et haute résistance mécanique à haute température continue.",
    pt: "Excelente resistência à oxidação e estabilidade mecânica sob alta temperatura contínua."
  },
  pittingPREN: "24-26"
}, {
  grade: "Hastelloy C-276",
  name: {
    en: "Nickel-Moly-Chromium Superalloy",
    zh: "镍钼铬高耐蚀哈氏超合金",
    es: "Superaleación de níquel-molibdeno-cromo",
    de: "Nickel-Molybdän-Chrom-Superlegierung",
    fr: "Superalliage nickel-molybdène-chrome",
    pt: "Superliga de níquel-molibdênio-cromo"
  },
  corrosionRating: {
    en: "Extreme Chemical",
    zh: "极限强酸与强氧化腐蚀",
    es: "Química extrema",
    de: "Extrem chemisch beständig",
    fr: "Chimique extrême",
    pt: "Química extrema"
  },
  primaryApplication: {
    en: "Chlorination Systems & Harsh Chemical Processing",
    zh: "湿氯气系统、烟气净化与严苛精细化工",
    es: "Sistemas de cloración y procesos químicos severos",
    de: "Chlorierungsanlagen & anspruchsvolle chemische Prozesse",
    fr: "Systèmes de chloration et procédés chimiques sévères",
    pt: "Sistemas de cloração e processos químicos severos"
  },
  characteristics: {
    en: "Resistant to wet chlorine gas, hypochlorite, and strong oxidizers.",
    zh: "可耐受湿氯气、次氯酸盐溶液及多种强氧化性无机酸的苛刻工况。",
    es: "Resistente al cloro húmedo, hipoclorito y oxidantes fuertes.",
    de: "Beständig gegen feuchtes Chlorgas, Hypochlorit und starke Oxidationsmittel.",
    fr: "Résistant au chlore gazeux humide, à l'hypochlorite et aux oxydants puissants.",
    pt: "Resistente a cloro gasoso úmido, hipoclorito e fortes oxidantes."
  },
  pittingPREN: "68+"
}];
interface SpecItem {
  label: Record<Lang, string>;
  value: string;
}
const specStripItems: SpecItem[] = [{
  label: {
    en: "Mesh Range",
    zh: "方孔目数范围",
    es: "Rango de malla",
    de: "Maschenbereich",
    fr: "Gamme de mailles",
    pt: "Faixa de malha"
  },
  value: "1 – 635 Mesh"
}, {
  label: {
    en: "Wire Diameter",
    zh: "线径规格范围",
    es: "Diámetro de alambre",
    de: "Drahtdurchmesser",
    fr: "Diamètre de fil",
    pt: "Diâmetro do arame"
  },
  value: "0.020 – 2.0 mm"
}, {
  label: {
    en: "Calibrated Aperture",
    zh: "校准开孔孔径",
    es: "Abertura calibrada",
    de: "Kalibrierte Maschenweite",
    fr: "Ouverture calibrée",
    pt: "Abertura calibrada"
  },
  value: "20 µm – 25 mm"
}, {
  label: {
    en: "Standard Grades",
    zh: "标准执行材质",
    es: "Grados estándar",
    de: "Standardwerkstoffe",
    fr: "Nuances standard",
    pt: "Ligas padrão"
  },
  value: "AISI 304 / 304L / 316 / 316L / 314"
}];
const copy = {
  badgeCategory: {
    en: "Alloy Compatibility",
    zh: "合金耐蚀选型",
    es: "Compatibilidad de aleaciones",
    de: "Werkstoffkompatibilität",
    fr: "Compatibilité des alliages",
    pt: "Compatibilidade de ligas"
  },
  badgeStandard: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  },
  h1: {
    en: "Select the exact alloy for your operating chemistry.",
    zh: "根据工况介质精确选配丝网合金材质",
    es: "Seleccione la aleación exacta para la química de su proceso.",
    de: "Wählen Sie die exakte Legierung für Ihre Prozesschemie.",
    fr: "Sélectionnez l'alliage exact pour la chimie de votre procédé.",
    pt: "Selecione a liga exata para a química de sua operação."
  },
  lead: {
    en: "Filtration lifespan depends on alloy suitability. Compare corrosion limits and chloride threshold ratings before sizing your stainless steel wire mesh.",
    zh: "过滤介质的使用寿命取决于合金选型的匹配度。在确定不锈钢丝网规格前，请对比耐腐蚀极限和氯化物耐受阈值。",
    es: "La vida útil de la filtración depende de la idoneidad de la aleación. Compare los límites de corrosión y los umbrales de cloruros antes de dimensionar su malla de alambre de acero inoxidable.",
    de: "Die Standzeit eines Filtermediums hängt von der Werkstoffeignung ab. Vergleichen Sie Korrosionsgrenzen und Chloridbeständigkeit vor der Auslegung Ihres Edelstahl-Drahtgewebes.",
    fr: "La durée de vie de la filtration dépend de l'adéquation de l'alliage. Comparez les limites de corrosion et la tolérance aux chlorures avant de dimensionner votre toile métallique en acier inoxydable.",
    pt: "A vida útil da filtração depende da adequação da liga. Compare os limites de corrosão e a tolerância a cloretos antes de dimensionar sua malha de arame de aço inoxidável."
  },
  viewMatrix: {
    en: "Materials & Grades Guide",
    zh: "材料与牌号指南",
    es: "Guía de materiales y grados",
    de: "Werkstoff- & Güteleitfaden",
    fr: "Guide des matériaux et nuances",
    pt: "Guia de materiais e ligas"
  },
  consoleTitle: {
    en: "Alloy Verification Console",
    zh: "合金性能验证控制台",
    es: "Consola de verificación de aleaciones",
    de: "Legierungs-Prüfkonsole",
    fr: "Console de vérification des alliages",
    pt: "Painel de verificação de ligas"
  },
  consoleSubtitle: {
    en: "Filter mesh performance benchmarks",
    zh: "过滤丝网性能基准与技术参数",
    es: "Puntos de referencia de rendimiento de mallas filtrantes",
    de: "Leistungs-Benchmarks für Filtergewebe",
    fr: "Repères de performance des toiles filtrantes",
    pt: "Parâmetros de desempenho de malhas filtrantes"
  },
  acidResistance: {
    en: "Acid Resistance",
    zh: "耐酸腐蚀等级",
    es: "Resistencia a ácidos",
    de: "Säurebeständigkeit",
    fr: "Résistance aux acides",
    pt: "Resistência a ácidos"
  },
  targetDuty: {
    en: "Target duty:",
    zh: "推荐应用场景:",
    es: "Aplicación recomendada:",
    de: "Empfohlener Einsatz:",
    fr: "Usage recommandé :",
    pt: "Aplicação recomendada:"
  },
  orderSpecNote: {
    en: "Full metallurgical traceability and material test reports available when specified in the order.",
    zh: "可按订单技术协议要求提供完整熔炼炉号材质质量追溯与测试报告。",
    es: "Trazabilidad metalúrgica completa y reportes de prueba disponibles previa especificación en el pedido.",
    de: "Vollständige Schmelzen-Rückverfolgbarkeit und Prüfzeugnisse lieferbar bei entsprechender Bestellangabe.",
    fr: "Traçabilité métallurgique complète et rapports d'essais matières disponibles sur spécification à la commande.",
    pt: "Rastreabilidade metalúrgica completa e relatórios de ensaio disponíveis mediante especificação no pedido."
  },
  cardFooterNote: {
    en: "Rolls, slit coils, stamped discs & finished cylinders",
    zh: "原卷、分条带卷、精密冲压圆片与成品滤筒",
    es: "Rollos, bobinas cortadas, discos estampados y cilindros terminados",
    de: "Rollen, Spaltbänder, Stanzronden & fertige Filterzylinder",
    fr: "Rouleaux, bandes refendues, disques découpés et cylindres finis",
    pt: "Rolos, bobinas cortadas, discos estampados e cilindros acabados"
  },
  formatsLink: {
    en: "Formats",
    zh: "供货规格",
    es: "Formatos",
    de: "Formate",
    fr: "Formats",
    pt: "Formatos"
  }
};
export default function CompatibilityHero() {
  const {
    lang
  } = useLanguage();
  const [selectedGrade, setSelectedGrade] = useState("AISI 316L");
  const activeMaterial = materialOptions.find(m => m.grade === selectedGrade) || materialOptions[0];
  return <section id="heromaterialcompatibility" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column: Heading & CTAs */}
          <div className="lg:col-span-7 flex flex-col items-start">
            <Reveal delay={0}>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, copy.badgeCategory)}</span>
                </Badge>
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                  <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, copy.badgeStandard)}</span>
                </Badge>
              </div>

              <h1 className="font-default text-balance mf-h1 text-foreground">
                {pick(lang, copy.h1)}
              </h1>

              <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
                {pick(lang, copy.lead)}
              </p>

              <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button size="lg" asChild className="font-semibold transition-colors">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>

                <Button variant="outline" size="lg" asChild className="font-semibold transition-colors">
                  <Link to="/MaterialsAndGradesPage" className="inline-flex items-center gap-2">
                    <FileText className="size-4" aria-hidden="true" />
                    <span>{pick(lang, copy.viewMatrix)}</span>
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Interactive Material Verification Card */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={60}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card">
                <CardHeader className="border-b border-border bg-muted/40 p-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {pick(lang, copy.consoleTitle)}
                      </CardTitle>
                      <CardDescription className="mf-caption text-muted-foreground mt-0.5">
                        {pick(lang, copy.consoleSubtitle)}
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className="font-mono mf-caption bg-card text-foreground border-border">
                      ISO 9044 / ASTM
                    </Badge>
                  </div>

                  {/* Grade Selector Tabs */}
                  <div className="grid grid-cols-4 gap-1.5 mt-3">
                    {materialOptions.map((mat, i) => <Button key={mat.grade} data-index={i} type="button" data-compact onClick={() => setSelectedGrade(mat.grade)} className={`h-auto mf-caption font-mono py-1.5 px-2 rounded-md transition-[color,background-color,border-color] text-center font-medium border ${selectedGrade === mat.grade ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:bg-muted"}`}>
                        {mat.grade}
                      </Button>)}
                  </div>
                </CardHeader>

                <CardContent className="p-4 space-y-4">
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="mf-h3 font-semibold text-foreground">{activeMaterial.grade}</span>
                        <span className="mf-caption font-mono px-2 py-0.5 rounded bg-muted text-foreground border border-border">
                          PREN {activeMaterial.pittingPREN}
                        </span>
                      </div>
                      <p className="mf-caption text-muted-foreground mt-0.5">
                        {pick(lang, activeMaterial.name)}
                      </p>
                    </div>

                    <p className="mf-caption text-foreground bg-muted/50 p-2.5 rounded-md border border-border leading-relaxed">
                      {pick(lang, activeMaterial.characteristics)}
                    </p>

                    <div className="grid grid-cols-1 gap-2.5 pt-1">
                      <div className="p-2.5 rounded-md border border-border bg-background">
                        <div className="flex items-center text-muted-foreground gap-1.5 mf-caption font-mono">
                          <FlaskConical className="size-3.5 text-primary" aria-hidden="true" />
                          <span>{pick(lang, copy.acidResistance)}</span>
                        </div>
                        <p className="mf-caption font-semibold text-foreground mt-1 line-clamp-1">
                          {pick(lang, activeMaterial.corrosionRating)}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-1.5 pt-2 border-t border-border">
                      <div className="flex items-start gap-2 mf-caption text-foreground">
                        <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>
                          <strong>{pick(lang, copy.targetDuty)}</strong>{" "}
                          {pick(lang, activeMaterial.primaryApplication)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 mf-caption text-foreground">
                        <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{pick(lang, copy.orderSpecNote)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Single Footer Line */}
                  <div className="flex items-center justify-between border-t border-border pt-3 mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Layers className="size-3.5 text-primary" aria-hidden="true" />
                      <span className="line-clamp-1">{pick(lang, copy.cardFooterNote)}</span>
                    </div>
                    <Link to="/SlitCoilsAndStampedFilterDiscsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                      <span>{pick(lang, copy.formatsLink)}</span>
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {specStripItems.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{pick(lang, spec.label)}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}